/**
 * Schedule ↔ cron conversion for the in-chat routine schedule card
 * (`ui_schedule_input`). The card collects structured fields (frequency, time,
 * custom repeat) and this module derives the 5-field cron the backend
 * schedules with, plus the human label shown everywhere ("Daily · 9:30 AM").
 * Deterministic on purpose: the LLM never derives cron itself — it forwards
 * the card's `cron` output verbatim to `routine_create_routine`.
 */

import type { ScheduleDay, ScheduleFrequency, ScheduleCustomConfig } from '@/types/ui-blocks';

export interface ScheduleSelection {
  frequency: ScheduleFrequency;
  /** 24h "HH:MM". */
  time: string;
  custom?: ScheduleCustomConfig | null;
}

/** Cron day-of-week numbers (0 = Sunday). */
const DAY_TO_CRON: Record<ScheduleDay, number> = {
  sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
};

const DAY_LABELS: Record<ScheduleDay, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

/** Week order used for the day-of-week toggle row (mock shows M T W T F S S). */
export const WEEK_DAYS: ScheduleDay[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/** Parse "HH:MM" (24h) into minute/hour; falls back to 09:00 on malformed input. */
function parseTime(time: string): { minute: number; hour: number } {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  const hour = match ? Number(match[1]) : 9;
  const minute = match ? Number(match[2]) : 0;
  if (!match || hour > 23 || minute > 59) return { minute: 0, hour: 9 };
  return { minute, hour };
}

/** "09:30" → "9:30 AM". */
export function formatTimeLabel(time: string): string {
  const { minute, hour } = parseTime(time);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
}

/**
 * Derive the 5-field cron for a schedule selection.
 *
 * - daily     → `m h * * *`
 * - weekdays  → `m h * * 1-5`
 * - weekly    → `m h * * 1` (Monday)
 * - monthly   → `m h 1 * *` (the 1st)
 * - custom    → unit "day": every-N-days step on the day-of-month field;
 *               unit "week": `m h * * d1,d2` (interval is always 1 — cron
 *               can't express "every N weeks"); unit "month": day 1 with an
 *               every-N-months step on the month field
 */
export function buildCronFromSchedule(selection: ScheduleSelection): string {
  const { minute, hour } = parseTime(selection.time);
  const base = `${minute} ${hour}`;

  switch (selection.frequency) {
    case 'daily':
      return `${base} * * *`;
    case 'weekdays':
      return `${base} * * 1-5`;
    case 'weekly':
      return `${base} * * 1`;
    case 'monthly':
      return `${base} 1 * *`;
    case 'custom': {
      const interval = Math.max(1, Math.floor(selection.custom?.interval ?? 1));
      const unit = selection.custom?.unit ?? 'week';
      if (unit === 'day') {
        return interval === 1 ? `${base} * * *` : `${base} */${interval} * *`;
      }
      if (unit === 'month') {
        return interval === 1 ? `${base} 1 * *` : `${base} 1 */${interval} *`;
      }
      const days = (selection.custom?.days_of_week ?? []).map((d) => DAY_TO_CRON[d]);
      const sorted = [...new Set(days)].sort((a, b) => a - b);
      return sorted.length > 0 ? `${base} * * ${sorted.join(',')}` : `${base} * * 1`;
    }
  }
}

/** Human label for a schedule selection, e.g. "Daily · 9:30 AM". */
export function describeSchedule(selection: ScheduleSelection): string {
  const time = formatTimeLabel(selection.time);

  switch (selection.frequency) {
    case 'daily':
      return `Daily · ${time}`;
    case 'weekdays':
      return `Weekdays · ${time}`;
    case 'weekly':
      return `Weekly on Mon · ${time}`;
    case 'monthly':
      return `Monthly on the 1st · ${time}`;
    case 'custom': {
      const interval = Math.max(1, Math.floor(selection.custom?.interval ?? 1));
      const unit = selection.custom?.unit ?? 'week';
      if (unit === 'week') {
        const days = (selection.custom?.days_of_week ?? []);
        const dayText = days.length > 0
          ? ` on ${days.map((d) => DAY_LABELS[d]).join(', ')}`
          : ' on Mon';
        return `Weekly${dayText} · ${time}`;
      }
      const every = interval === 1 ? `Every ${unit}` : `Every ${interval} ${unit}s`;
      return `${every} · ${time}`;
    }
  }
}

/**
 * Best-effort human label for a stored cron, for schedules created via the
 * card (e.g. "30 9 * * 1-5" → "Weekdays · 9:30 AM"). Returns null when the
 * cron doesn't match a card-producible shape — callers keep their own
 * fallback (the legacy FREQUENCY_OPTIONS lookup / raw cron).
 */
export function describeCron(cron: string): string | null {
  const match = /^(\d{1,2}) (\d{1,2}) (\S+) (\S+) (\S+)$/.exec(cron.trim());
  if (!match) return null;

  const [, minute, hour, dayOfMonth, month, dayOfWeek] = match;
  const time = formatTimeLabel(`${hour}:${minute.padStart(2, '0')}`);
  const cronToDay = new Map<string, ScheduleDay>(
    (Object.entries(DAY_TO_CRON) as [ScheduleDay, number][]).map(([day, num]) => [String(num), day])
  );

  if (dayOfMonth === '*' && month === '*') {
    if (dayOfWeek === '*') return `Daily · ${time}`;
    if (dayOfWeek === '1-5') return `Weekdays · ${time}`;
    const days = dayOfWeek.split(',').map((d) => cronToDay.get(d));
    if (days.every((d): d is ScheduleDay => Boolean(d))) {
      return `Weekly on ${days.map((d) => DAY_LABELS[d]).join(', ')} · ${time}`;
    }
    return null;
  }

  if (dayOfMonth === '1' && month === '*' && dayOfWeek === '*') {
    return `Monthly on the 1st · ${time}`;
  }

  return null;
}
